// src/features/professional/components/ItemForm.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AttachFile as AttachIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../styles/theme';

const ItemForm = ({ type, initialData, onSubmit, onCancel, submitting }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ...initialData
  });
  const [newFiles, setNewFiles] = useState([]);
  const [newSkill, setNewSkill] = useState('');

  const qualificationTypes = [
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'Doctoral Degree',
    'Professional Diploma',
    'Certificate',
    'Professional Certification',
    'Short Course',
    'Workshop',
    'Conference Attendance'
  ];

  const projectTypes = [
    'Curriculum Development',
    'Research Project',
    'Professional Development Programme',
    'Community Outreach',
    'Educational Technology Initiative',
    'Assessment Development',
    'School Improvement Project',
    'Other'
  ];

  const projectStatuses = ['Planning', 'In Progress', 'Completed', 'On Hold'];

  const milestoneCategories = [
    'Promotion',
    'Award/Recognition',
    'Professional Achievement',
    'Educational Milestone',
    'Leadership Role',
    'Other'
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        skills: initialData.skills || [],
        attachments: initialData.attachments || []
      });
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setNewFiles(prev => [...prev, ...files]);
  };

  const removeNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills?.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...(prev.skills || []), newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formData, newFiles);
  };

  const renderQualificationForm = () => (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Qualification Title *"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Institution *"
            value={formData.institution || ''}
            onChange={(e) => handleInputChange('institution', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type || ''}
              onChange={(e) => handleInputChange('type', e.target.value)}
              label="Type"
            >
              {qualificationTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Year Completed *"
            value={formData.year || ''}
            onChange={(e) => handleInputChange('year', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Grade/Result"
            value={formData.grade || ''}
            onChange={(e) => handleInputChange('grade', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe the qualification, its relevance to your teaching practice..."
          />
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Skills Acquired
            </Typography>
            <Box display="flex" gap={1} mb={1} flexWrap="wrap">
              {formData.skills?.map((skill, index) => (
                <Chip
                  key={index}
                  label={skill}
                  onDelete={() => removeSkill(skill)}
                  size="small"
                />
              ))}
            </Box>
            <Box display="flex" gap={1}>
              <TextField
                size="small"
                label="Add skill"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSkill();
                  }
                }}
              />
              <Button onClick={addSkill} disabled={!newSkill.trim()}>
                <AddIcon />
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );

  const renderMilestoneForm = () => (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Milestone Title *"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label="Date *"
            value={formData.date || ''}
            onChange={(e) => handleInputChange('date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Organization *"
            value={formData.organization || ''}
            onChange={(e) => handleInputChange('organization', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category || ''}
              onChange={(e) => handleInputChange('category', e.target.value)}
              label="Category"
            >
              {milestoneCategories.map((category) => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description *"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe the milestone and its significance..."
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Impact"
            value={formData.impact || ''}
            onChange={(e) => handleInputChange('impact', e.target.value)}
            placeholder="What impact did this milestone have on your career or practice?"
          />
        </Grid>
      </Grid>
    </>
  );

  const renderProjectForm = () => (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Project Title *"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Project Type</InputLabel>
            <Select
              value={formData.type || ''}
              onChange={(e) => handleInputChange('type', e.target.value)}
              label="Project Type"
            >
              {projectTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status || ''}
              onChange={(e) => handleInputChange('status', e.target.value)}
              label="Status"
            >
              {projectStatuses.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Duration"
            value={formData.duration || ''}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            placeholder="e.g., 3 months, 2023-2024"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Your Role"
            value={formData.role || ''}
            onChange={(e) => handleInputChange('role', e.target.value)}
            placeholder="e.g., Project Lead, Team Member"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description *"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe the project objectives, scope, and your involvement..."
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Outcomes"
            value={formData.outcomes || ''}
            onChange={(e) => handleInputChange('outcomes', e.target.value)}
            placeholder="What were the results and impact of this project?"
          />
        </Grid>
      </Grid>
    </>
  );

  const renderFileUpload = () => (
    <Box mt={2}>
      <Typography variant="subtitle2" gutterBottom>
        Attachments
      </Typography>
      
      {/* Existing attachments */}
      {formData.attachments && formData.attachments.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Current attachments:
          </Typography>
          <List dense>
            {formData.attachments.map((file, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <AttachIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={file.name} />
                <ListItemSecondaryAction>
                  <IconButton 
                    size="small" 
                    onClick={() => removeExistingAttachment(index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* New file uploads */}
      {newFiles.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            New files to upload:
          </Typography>
          <List dense>
            {newFiles.map((file, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <AttachIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={file.name} />
                <ListItemSecondaryAction>
                  <IconButton 
                    size="small" 
                    onClick={() => removeNewFile(index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Button
        component="label"
        variant="outlined"
        startIcon={<UploadIcon />}
        sx={{ borderColor: THEME_COLORS.primary, color: THEME_COLORS.primary }}
      >
        Upload Files
        <input
          type="file"
          hidden
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={handleFileUpload}
        />
      </Button>
      <Typography variant="caption" display="block" color="textSecondary" mt={1}>
        Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB per file)
      </Typography>
    </Box>
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {type === 'qualification' && renderQualificationForm()}
      {type === 'milestone' && renderMilestoneForm()}
      {type === 'project' && renderProjectForm()}
      
      {renderFileUpload()}

      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={submitting || !formData.title}
          sx={{ backgroundColor: THEME_COLORS.primary }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            `${initialData ? 'Update' : 'Add'} ${type}`
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ItemForm;